import type { PostType, PostTypeSpec, MediaSpec, CopySpec, VideoSpec, Tier } from "./types.js";

// Common transcode targets. Bitrates chosen to keep files well under each
// platform's max-bytes limit while preserving native-quality look.
const vid9x16_5M: VideoSpec = { width: 1080, height: 1920, maxBitrateKbps: 5000, maxDurationSec: 90, container: "mp4" };
const vidTikTok: VideoSpec = { width: 1080, height: 1920, maxBitrateKbps: 6000, maxDurationSec: 600, container: "mp4" };
const vidShort: VideoSpec = { width: 1080, height: 1920, maxBitrateKbps: 8000, maxDurationSec: 60, container: "mp4" };
const vidLong: VideoSpec = { width: 1920, height: 1080, maxBitrateKbps: 10000, maxDurationSec: 12 * 3600, container: "mp4" };

// Common media presets, parameterised so per-type tweaks are obvious.
const ig: MediaSpec = {
  kind: "image",
  minItems: 1,
  maxItems: 1,
  preferredAspect: 1,
  allowedAspect: [0.8, 1.91],
  maxWidth: 1440,
  maxBytes: 8 * 1024 * 1024,
};
const igCarousel: MediaSpec = { ...ig, minItems: 2, maxItems: 10 };
const igVertical: MediaSpec = {
  kind: "video",
  minItems: 1,
  maxItems: 1,
  preferredAspect: 9 / 16,
  allowedAspect: [9 / 16, 9 / 16],
  maxWidth: 1080,
  maxBytes: 100 * 1024 * 1024,
};
const fb: MediaSpec = {
  kind: "either",
  minItems: 0,
  maxItems: 1,
  preferredAspect: 1.91,
  allowedAspect: [0.5625, 1.91],
  maxWidth: 2048,
  maxBytes: 100 * 1024 * 1024,
};
const linkedin: MediaSpec = {
  kind: "either",
  minItems: 0,
  maxItems: 1,
  preferredAspect: 1.91,
  allowedAspect: [0.4, 2.4],
  maxWidth: 1200,
  maxBytes: 100 * 1024 * 1024,
};
const x: MediaSpec = {
  kind: "either",
  minItems: 0,
  maxItems: 4,
  preferredAspect: 16 / 9,
  allowedAspect: [0.5, 2.39],
  maxWidth: 4096,
  maxBytes: 5 * 1024 * 1024,
};
const tiktokVideo: MediaSpec = {
  kind: "video",
  minItems: 1,
  maxItems: 1,
  preferredAspect: 9 / 16,
  allowedAspect: [9 / 16, 9 / 16],
  maxWidth: 1080,
  maxBytes: 287 * 1024 * 1024,
};
const tiktokPhoto: MediaSpec = {
  kind: "image",
  minItems: 1,
  maxItems: 35,
  preferredAspect: 1,
  allowedAspect: [0.5625, 1.91],
  maxWidth: 1080,
  maxBytes: 20 * 1024 * 1024,
};
const youtubeShort: MediaSpec = { ...igVertical, maxBytes: 256 * 1024 * 1024 };
const youtubeLong: MediaSpec = {
  kind: "video",
  minItems: 1,
  maxItems: 1,
  preferredAspect: 16 / 9,
  allowedAspect: [0.5, 2.39],
  maxWidth: 3840,
  maxBytes: 128 * 1024 * 1024 * 1024, // YouTube allows up to 128 GB
};
const noMedia: MediaSpec = {
  kind: "none",
  minItems: 0,
  maxItems: 0,
  preferredAspect: 1,
  allowedAspect: [0.5, 2.5],
  maxWidth: 0,
  maxBytes: 0,
};

const capIG: CopySpec = { captionMaxChars: 2200, hashtagsAllowed: true };
const capFB: CopySpec = { captionMaxChars: 63206, hashtagsAllowed: true };
const capLI: CopySpec = { captionMaxChars: 3000, hashtagsAllowed: true };
const capX: CopySpec = { captionMaxChars: 280, hashtagsAllowed: true };
const capXThread: CopySpec = {
  captionMaxChars: 280,
  hashtagsAllowed: true,
  threaded: true,
  threadItemMaxChars: 280,
  threadMaxItems: 25,
};
const capThreads: CopySpec = { captionMaxChars: 500, hashtagsAllowed: true };
const capThreadsThread: CopySpec = {
  captionMaxChars: 500,
  hashtagsAllowed: true,
  threaded: true,
  threadItemMaxChars: 500,
  threadMaxItems: 25,
};
const capTikTok: CopySpec = { captionMaxChars: 4000, hashtagsAllowed: true };
const capYouTubeShort: CopySpec = {
  captionMaxChars: 1000,
  titleMaxChars: 100,
  hashtagsAllowed: true,
};
const capYouTubeLong: CopySpec = {
  captionMaxChars: 0,
  titleMaxChars: 100,
  descriptionMaxChars: 5000,
  hashtagsAllowed: true,
};
const capReddit: CopySpec = {
  captionMaxChars: 40000,
  titleMaxChars: 300,
  hashtagsAllowed: false,
};
const capGBP: CopySpec = { captionMaxChars: 1500, hashtagsAllowed: false };
const capLIDoc: CopySpec = {
  captionMaxChars: 3000,
  titleMaxChars: 200,
  hashtagsAllowed: true,
};

export const POST_TYPE_REGISTRY: Record<PostType, PostTypeSpec> = {
  // Tier 1
  IG_FEED: { id: "IG_FEED", platform: "INSTAGRAM", label: "Instagram feed", tier: 1, media: ig, copy: capIG },
  FB_FEED: { id: "FB_FEED", platform: "FACEBOOK", label: "Facebook feed", tier: 1, media: fb, copy: capFB },
  LINKEDIN_TEXT_OR_IMAGE: {
    id: "LINKEDIN_TEXT_OR_IMAGE",
    platform: "LINKEDIN",
    label: "LinkedIn post",
    tier: 1,
    media: linkedin,
    copy: capLI,
  },
  X_TWEET: { id: "X_TWEET", platform: "TWITTER", label: "X tweet", tier: 1, media: x, copy: capX },
  THREADS_POST: { id: "THREADS_POST", platform: "THREADS", label: "Threads post", tier: 1, media: x, copy: capThreads },
  REDDIT_LINK: {
    id: "REDDIT_LINK",
    platform: "REDDIT",
    label: "Reddit link post",
    tier: 1,
    media: noMedia,
    copy: capReddit,
    requiredKnobs: ["subreddit", "url"],
  },
  REDDIT_MEDIA: {
    id: "REDDIT_MEDIA",
    platform: "REDDIT",
    label: "Reddit media post",
    tier: 1,
    media: { ...fb, maxItems: 1 },
    copy: capReddit,
    requiredKnobs: ["subreddit"],
  },
  GBP_UPDATE: {
    id: "GBP_UPDATE",
    platform: "GOOGLEBUSINESS",
    label: "Google Business update",
    tier: 1,
    media: fb,
    copy: capGBP,
  },

  // Tier 2
  IG_CAROUSEL: {
    id: "IG_CAROUSEL",
    platform: "INSTAGRAM",
    label: "Instagram carousel",
    tier: 2,
    media: igCarousel,
    copy: capIG,
  },
  LINKEDIN_DOC: {
    id: "LINKEDIN_DOC",
    platform: "LINKEDIN",
    label: "LinkedIn document carousel",
    tier: 2,
    media: { kind: "either", minItems: 1, maxItems: 1, preferredAspect: 1, allowedAspect: [0.5, 2], maxWidth: 1200, maxBytes: 100 * 1024 * 1024 },
    copy: capLIDoc,
    requiredKnobs: ["documentTitle"],
  },
  X_THREAD: { id: "X_THREAD", platform: "TWITTER", label: "X thread", tier: 2, media: x, copy: capXThread },
  THREADS_THREAD: {
    id: "THREADS_THREAD",
    platform: "THREADS",
    label: "Threads thread",
    tier: 2,
    media: x,
    copy: capThreadsThread,
  },
  REDDIT_SELF: {
    id: "REDDIT_SELF",
    platform: "REDDIT",
    label: "Reddit self post",
    tier: 2,
    media: noMedia,
    copy: capReddit,
    requiredKnobs: ["subreddit"],
  },

  // Tier 3
  IG_REEL: { id: "IG_REEL", platform: "INSTAGRAM", label: "Instagram Reel", tier: 3, media: igVertical, copy: capIG, video: vid9x16_5M },
  IG_STORY: { id: "IG_STORY", platform: "INSTAGRAM", label: "Instagram Story", tier: 3, media: igVertical, copy: capIG, video: vid9x16_5M, isStory: true },
  FB_REEL: { id: "FB_REEL", platform: "FACEBOOK", label: "Facebook Reel", tier: 3, media: igVertical, copy: capFB, video: vid9x16_5M },
  FB_STORY: { id: "FB_STORY", platform: "FACEBOOK", label: "Facebook Story", tier: 3, media: igVertical, copy: capFB, video: vid9x16_5M, isStory: true },
  TIKTOK_VIDEO: {
    id: "TIKTOK_VIDEO",
    platform: "TIKTOK",
    label: "TikTok video",
    tier: 3,
    media: tiktokVideo,
    copy: capTikTok,
    video: vidTikTok,
    requiredKnobs: ["privacyLevel", "contentPreviewConfirmed", "expressConsentGiven"],
  },
  TIKTOK_PHOTO: { id: "TIKTOK_PHOTO", platform: "TIKTOK", label: "TikTok photo carousel", tier: 3, media: tiktokPhoto, copy: capTikTok },
  YOUTUBE_SHORT: {
    id: "YOUTUBE_SHORT",
    platform: "YOUTUBE",
    label: "YouTube Short",
    tier: 3,
    media: youtubeShort,
    copy: capYouTubeShort,
    video: vidShort,
  },
  YOUTUBE_LONG: {
    id: "YOUTUBE_LONG",
    platform: "YOUTUBE",
    label: "YouTube long-form",
    tier: 3,
    media: youtubeLong,
    copy: capYouTubeLong,
    video: vidLong,
    requiredKnobs: ["madeForKids"],
  },
};

export function getPostTypeSpec(id: PostType): PostTypeSpec {
  return POST_TYPE_REGISTRY[id];
}

export function postTypesForPlatform(platform: string): PostTypeSpec[] {
  return Object.values(POST_TYPE_REGISTRY).filter((s) => s.platform === platform);
}

export function postTypesByTier(tier: Tier): PostTypeSpec[] {
  return Object.values(POST_TYPE_REGISTRY).filter((s) => s.tier === tier);
}
